import React from "react";
import { connect } from "react-redux";

import MainContainer from "../../components/mainContainer/index";
import inits from "../../engine/inits";

import { fetchUrl } from "../../config/utils";
import { nameClases } from "../../config/config";

import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
    faComments,
    faChevronRight,
    faPlus,
    faArrowLeft,
    faUserCircle,
    faClock,
    faReply,
    faCrown
} from "@fortawesome/free-solid-svg-icons";

import style from "./style.scss";

// ════════════════════════════════════════════════════════════════
// Foro — single-page state machine. Views:
//   - "categories"  → lista de categorías
//   - "threads"     → lista de threads en una categoría
//   - "thread"      → vista de thread con posts + form de respuesta
//   - "newThread"   → form para crear thread en una categoría
// Atrium Frame minimalista, paleta dorada tenue, sin glows excesivos.
// ════════════════════════════════════════════════════════════════

// Formato relativo de fecha — "hace N min/horas/días" o fecha plana
// si es muy viejo. Para mostrar lastActivityAt en categorías y posts.
function timeAgo(date) {
    if (!date) return "—";
    const ts = new Date(date).getTime();
    const diff = Date.now() - ts;
    if (diff < 0) return "ahora";
    const s = Math.floor(diff / 1000);
    if (s < 60) return s + "s";
    const m = Math.floor(s / 60);
    if (m < 60) return m + "min";
    const h = Math.floor(m / 60);
    if (h < 24) return h + "h";
    const d = Math.floor(h / 24);
    if (d < 30) return d + "d";
    return new Date(ts).toLocaleDateString("es-AR");
}

// Resuelve el sprite del head front-facing desde inits (mismo lookup
// que ranking / wiki). Devuelve null si inits no están listos.
// Acepta un objeto char (con idHead + idLastHead) o un idHead directo
// para que el caller pueda pasar lo que tenga a mano. Si el char está
// navegando/muerto, idHead=0 y la cara real vive en idLastHead → fallback.
function getHeadSpriteStyle(charOrIdHead) {
    let idHead;
    if (typeof charOrIdHead === "object" && charOrIdHead !== null) {
        idHead = charOrIdHead.idHead || charOrIdHead.idLastHead || 0;
    } else {
        idHead = charOrIdHead || 0;
    }
    if (!idHead || !inits.heads || !inits.heads[idHead]) return null;
    const grhIdx = inits.heads[idHead][2]; // direction 2 = front
    const graphic =
        grhIdx && inits.graphics && inits.graphics[grhIdx]
            ? inits.graphics[grhIdx]
            : null;
    if (!graphic) return null;
    return {
        backgroundImage: `url("/static/graficos/${graphic.numFile}.png")`,
        backgroundPosition: `-${graphic.sX || 0}px -${graphic.sY || 0}px`,
        width: `${graphic.width || 17}px`,
        height: `${graphic.height || 50}px`
    };
}

class ForumPage extends React.Component {
    static async getInitialProps({ req }) {
        const userAgent = req
            ? req.headers["user-agent"]
            : typeof navigator !== "undefined"
            ? navigator.userAgent
            : "";
        return { userAgent };
    }

    state = {
        view: "categories", // categories | threads | thread | newThread
        loading: true,
        error: null,

        // Data por view
        categories: [],
        activeCategory: null, // { slug, name, ... }
        threads: [],
        activeThread: null,
        posts: [],

        // Form state — crear thread o reply
        formTitle: "",
        formContent: "",
        formError: "",
        formSubmitting: false,

        // Selector de char — chars de la cuenta + cuál está seleccionado
        myChars: [],
        selectedCharId: null,
        charSelectorOpen: false
    };

    componentDidMount() {
        this._unmounted = false;
        this.fetchCategories();
        this.fetchMyChars();
    }

    componentWillUnmount() {
        this._unmounted = true;
    }

    // ── Data fetchers ──────────────────────────────────────────

    fetchCategories = async () => {
        try {
            this.setState({ loading: true, error: null });
            const cats = await fetchUrl("/forum/categories");
            if (this._unmounted) return;
            this.setState({
                categories: Array.isArray(cats) ? cats : [],
                loading: false
            });
        } catch (e) {
            if (this._unmounted) return;
            this.setState({ loading: false, error: "Error cargando categorías." });
        }
    };

    fetchThreads = async category => {
        try {
            this.setState({
                loading: true,
                error: null,
                view: "threads",
                activeCategory: category
            });
            const r = await fetchUrl(
                `/forum/category/${encodeURIComponent(category.slug)}/threads`
            );
            if (this._unmounted) return;
            this.setState({
                threads: (r && r.threads) || [],
                activeCategory: (r && r.category) || category,
                loading: false
            });
        } catch (e) {
            if (this._unmounted) return;
            this.setState({ loading: false, error: "Error cargando temas." });
        }
    };

    fetchThread = async thread => {
        try {
            this.setState({
                loading: true,
                error: null,
                view: "thread",
                activeThread: thread
            });
            const r = await fetchUrl(`/forum/thread/${thread._id}`);
            if (this._unmounted) return;
            this.setState({
                activeThread: (r && r.thread) || thread,
                posts: (r && r.posts) || [],
                loading: false
            });
        } catch (e) {
            if (this._unmounted) return;
            this.setState({ loading: false, error: "Error cargando tema." });
        }
    };

    fetchMyChars = async () => {
        try {
            // Solo si está logueado. fetchUrl con credentials para session.
            const r = await fetchUrl("/characters", {
                credentials: "include"
            });
            if (this._unmounted) return;
            if (Array.isArray(r)) {
                this.setState({
                    myChars: r,
                    selectedCharId: r[0] ? r[0]._id : null
                });
            }
        } catch (e) {
            // Si no está logueado, fetchMyChars falla — está OK, el form
            // de post pide login.
        }
    };

    // ── Navegación entre views ─────────────────────────────────

    goBack = () => {
        const { view, activeCategory } = this.state;
        if (view === "thread") {
            this.fetchThreads(activeCategory);
        } else if (view === "threads" || view === "newThread") {
            this.setState({
                view: "categories",
                activeCategory: null,
                threads: []
            });
        }
    };

    openNewThread = () => {
        this.setState({
            view: "newThread",
            formTitle: "",
            formContent: "",
            formError: ""
        });
    };

    // ── Form handlers ──────────────────────────────────────────

    submitNewThread = async () => {
        const {
            formTitle,
            formContent,
            selectedCharId,
            activeCategory
        } = this.state;
        if (!selectedCharId) {
            this.setState({
                formError:
                    "Tenés que elegir un personaje para postear."
            });
            return;
        }
        if (formTitle.trim().length < 3) {
            this.setState({
                formError: "Título muy corto (mín 3 caracteres)."
            });
            return;
        }
        if (formContent.trim().length < 1) {
            this.setState({
                formError: "El contenido no puede estar vacío."
            });
            return;
        }
        this.setState({ formSubmitting: true, formError: "" });
        try {
            const r = await fetchUrl("/forum/thread", {
                method: "POST",
                credentials: "include",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({
                    categorySlug: activeCategory.slug,
                    title: formTitle.trim(),
                    content: formContent.trim(),
                    idCharacter: selectedCharId
                })
            });
            if (this._unmounted) return;
            if (r && r.error) {
                this.setState({
                    formError: r.message || "Error",
                    formSubmitting: false
                });
                return;
            }
            // Éxito → navegamos al thread recién creado.
            this.setState({
                formSubmitting: false,
                formTitle: "",
                formContent: "",
                formError: ""
            });
            this.fetchThread(r);
        } catch (e) {
            if (this._unmounted) return;
            this.setState({
                formError: "Error de red al postear.",
                formSubmitting: false
            });
        }
    };

    submitReply = async () => {
        const { formContent, selectedCharId, activeThread } = this.state;
        if (!selectedCharId) {
            this.setState({
                formError: "Tenés que elegir un personaje para responder."
            });
            return;
        }
        if (formContent.trim().length < 1) {
            this.setState({
                formError: "El contenido no puede estar vacío."
            });
            return;
        }
        this.setState({ formSubmitting: true, formError: "" });
        try {
            const r = await fetchUrl(
                `/forum/thread/${activeThread._id}/reply`,
                {
                    method: "POST",
                    credentials: "include",
                    headers: { "Content-Type": "application/json" },
                    body: JSON.stringify({
                        content: formContent.trim(),
                        idCharacter: selectedCharId
                    })
                }
            );
            if (this._unmounted) return;
            if (r && r.error) {
                this.setState({
                    formError: r.message || "Error",
                    formSubmitting: false
                });
                return;
            }
            // Éxito → recargar el thread con el reply nuevo.
            this.setState({
                formContent: "",
                formSubmitting: false,
                formError: ""
            });
            this.fetchThread(activeThread);
        } catch (e) {
            if (this._unmounted) return;
            this.setState({
                formError: "Error de red al responder.",
                formSubmitting: false
            });
        }
    };

    // ── Char selector dropdown ─────────────────────────────────

    toggleCharSelector = () => {
        this.setState({ charSelectorOpen: !this.state.charSelectorOpen });
    };

    selectChar = idCharacter => {
        this.setState({
            selectedCharId: idCharacter,
            charSelectorOpen: false
        });
    };

    renderCharSelector = () => {
        const { myChars, selectedCharId, charSelectorOpen } = this.state;
        if (!myChars || myChars.length === 0) {
            return (
                <div className={style.charSelectorEmpty}>
                    Iniciá sesión y creá un personaje para postear.
                </div>
            );
        }
        const selected =
            myChars.find(c => c._id === selectedCharId) || myChars[0];
        const headSprite = getHeadSpriteStyle(selected);
        return (
            <div className={style.charSelectorWrap}>
                <button
                    type="button"
                    className={style.charSelectorButton}
                    onClick={this.toggleCharSelector}
                >
                    <div className={style.charSelectorAvatar}>
                        {headSprite ? (
                            <div
                                className={style.charSelectorSprite}
                                style={headSprite}
                            />
                        ) : (
                            <FontAwesomeIcon icon={faUserCircle} />
                        )}
                    </div>
                    <div className={style.charSelectorInfo}>
                        <span className={style.charSelectorName}>
                            {selected.name}
                        </span>
                        <span className={style.charSelectorMeta}>
                            {nameClases[selected.idClase] || "—"} · Lv{" "}
                            {selected.level || 1}
                        </span>
                    </div>
                    <FontAwesomeIcon
                        icon={faChevronRight}
                        className={`${style.charSelectorChevron} ${
                            charSelectorOpen
                                ? style.charSelectorChevronOpen
                                : ""
                        }`}
                    />
                </button>
                {charSelectorOpen && (
                    <div className={style.charSelectorDropdown}>
                        {myChars.map(c => {
                            const sprite = getHeadSpriteStyle(c);
                            return (
                                <button
                                    key={c._id}
                                    type="button"
                                    className={`${style.charSelectorItem} ${
                                        c._id === selectedCharId
                                            ? style.charSelectorItemActive
                                            : ""
                                    }`}
                                    onClick={() => this.selectChar(c._id)}
                                >
                                    <div
                                        className={style.charSelectorAvatar}
                                    >
                                        {sprite ? (
                                            <div
                                                className={
                                                    style.charSelectorSprite
                                                }
                                                style={sprite}
                                            />
                                        ) : (
                                            <FontAwesomeIcon
                                                icon={faUserCircle}
                                            />
                                        )}
                                    </div>
                                    <div className={style.charSelectorInfo}>
                                        <span
                                            className={
                                                style.charSelectorName
                                            }
                                        >
                                            {c.name}
                                        </span>
                                        <span
                                            className={
                                                style.charSelectorMeta
                                            }
                                        >
                                            {nameClases[c.idClase] || "—"} ·
                                            Lv {c.level || 1}
                                        </span>
                                    </div>
                                </button>
                            );
                        })}
                    </div>
                )}
            </div>
        );
    };

    // ── Render views ───────────────────────────────────────────

    renderCategoriesView = () => {
        const { categories, loading } = this.state;
        if (loading) {
            return <div className={style.loading}>Cargando categorías...</div>;
        }
        return (
            <div className={style.categoriesList}>
                {categories.map(cat => (
                    <button
                        key={cat._id}
                        type="button"
                        className={style.categoryRow}
                        onClick={() => this.fetchThreads(cat)}
                    >
                        <div className={style.categoryMain}>
                            <div className={style.categoryNameRow}>
                                <span className={style.categoryName}>
                                    {cat.name}
                                </span>
                                {cat.gmOnly ? (
                                    <span className={style.gmBadge}>
                                        <FontAwesomeIcon icon={faCrown} /> GM
                                    </span>
                                ) : null}
                            </div>
                            <span className={style.categoryDesc}>
                                {cat.description}
                            </span>
                        </div>
                        <div className={style.categoryStats}>
                            <div className={style.statItem}>
                                <span className={style.statValue}>
                                    {cat.threadCount || 0}
                                </span>
                                <span className={style.statLabel}>Temas</span>
                            </div>
                            <div className={style.statItem}>
                                <span className={style.statValue}>
                                    {cat.postCount || 0}
                                </span>
                                <span className={style.statLabel}>Posts</span>
                            </div>
                        </div>
                        <div className={style.categoryLast}>
                            {cat.lastActivityAt ? (
                                <React.Fragment>
                                    <span className={style.lastTitle}>
                                        {cat.lastThreadTitle}
                                    </span>
                                    <span className={style.lastMeta}>
                                        {cat.lastAuthorName} ·{" "}
                                        {timeAgo(cat.lastActivityAt)}
                                    </span>
                                </React.Fragment>
                            ) : (
                                <span className={style.lastEmpty}>
                                    Sin actividad
                                </span>
                            )}
                        </div>
                        <FontAwesomeIcon
                            icon={faChevronRight}
                            className={style.rowChevron}
                        />
                    </button>
                ))}
            </div>
        );
    };

    renderThreadsView = () => {
        const { threads, activeCategory, loading } = this.state;
        const canPost =
            activeCategory &&
            (!activeCategory.gmOnly ||
                // GM check rough — el server hace la check real, esto solo
                // es UX para ocultar el botón cuando seguro no van a poder.
                (this.state.myChars.some(
                    c => (c.privileges || 0) === 1
                )));
        return (
            <React.Fragment>
                <div className={style.threadsToolbar}>
                    <div className={style.toolbarTitle}>
                        {activeCategory && activeCategory.description}
                    </div>
                    {canPost && (
                        <button
                            type="button"
                            className={style.newThreadBtn}
                            onClick={this.openNewThread}
                        >
                            <FontAwesomeIcon icon={faPlus} /> Nuevo tema
                        </button>
                    )}
                </div>

                {loading ? (
                    <div className={style.loading}>Cargando temas...</div>
                ) : threads.length === 0 ? (
                    <div className={style.empty}>
                        Aún no hay temas en esta categoría. ¡Sé el primero!
                    </div>
                ) : (
                    <div className={style.threadsList}>
                        {threads.map(t => (
                            <button
                                key={t._id}
                                type="button"
                                className={`${style.threadRow} ${
                                    t.pinned ? style.threadPinned : ""
                                }`}
                                onClick={() => this.fetchThread(t)}
                            >
                                <div className={style.threadAvatar}>
                                    {getHeadSpriteStyle(t.authorIdHead) ? (
                                        <div
                                            className={style.threadSprite}
                                            style={getHeadSpriteStyle(
                                                t.authorIdHead
                                            )}
                                        />
                                    ) : (
                                        <FontAwesomeIcon
                                            icon={faUserCircle}
                                        />
                                    )}
                                </div>
                                <div className={style.threadMain}>
                                    <div className={style.threadTitle}>
                                        {t.pinned ? "📌 " : ""}
                                        {t.title}
                                    </div>
                                    <div className={style.threadMeta}>
                                        Por {t.authorCharacterName} ·{" "}
                                        {timeAgo(t.createdAt)}
                                    </div>
                                </div>
                                <div className={style.threadStats}>
                                    <div className={style.statItem}>
                                        <span className={style.statValue}>
                                            {t.replyCount || 0}
                                        </span>
                                        <span className={style.statLabel}>
                                            Resp
                                        </span>
                                    </div>
                                    <div className={style.statItem}>
                                        <span className={style.statValue}>
                                            {t.viewCount || 0}
                                        </span>
                                        <span className={style.statLabel}>
                                            Vistas
                                        </span>
                                    </div>
                                </div>
                                <div className={style.threadLast}>
                                    <span className={style.lastMeta}>
                                        <FontAwesomeIcon icon={faClock} />{" "}
                                        {timeAgo(t.lastReplyAt)}
                                    </span>
                                </div>
                            </button>
                        ))}
                    </div>
                )}
            </React.Fragment>
        );
    };

    renderThreadView = () => {
        const {
            activeThread,
            posts,
            loading,
            formContent,
            formSubmitting,
            formError
        } = this.state;
        if (!activeThread) return null;
        return (
            <React.Fragment>
                <div className={style.threadHeader}>
                    <h2 className={style.threadHeaderTitle}>
                        {activeThread.title}
                    </h2>
                    <div className={style.threadHeaderMeta}>
                        {activeThread.replyCount || 0} respuestas ·{" "}
                        {activeThread.viewCount || 0} vistas
                    </div>
                </div>

                {loading ? (
                    <div className={style.loading}>Cargando posts...</div>
                ) : (
                    <div className={style.postsList}>
                        {posts.map(p => (
                            <div key={p._id} className={style.postRow}>
                                <aside className={style.postAuthor}>
                                    <div className={style.postAvatar}>
                                        {getHeadSpriteStyle(p.authorIdHead) ? (
                                            <div
                                                className={style.postSprite}
                                                style={getHeadSpriteStyle(
                                                    p.authorIdHead
                                                )}
                                            />
                                        ) : (
                                            <FontAwesomeIcon
                                                icon={faUserCircle}
                                            />
                                        )}
                                    </div>
                                    <div className={style.postAuthorName}>
                                        {p.authorCharacterName}
                                        {p.authorPrivileges > 0 ? (
                                            <span
                                                className={style.gmBadgeMini}
                                                title="GM"
                                            >
                                                <FontAwesomeIcon
                                                    icon={faCrown}
                                                />
                                            </span>
                                        ) : null}
                                    </div>
                                    <div className={style.postAuthorMeta}>
                                        {nameClases[p.authorIdClase] || "—"}
                                    </div>
                                    <div className={style.postAuthorLevel}>
                                        Nivel {p.authorLevel || 1}
                                    </div>
                                </aside>
                                <div className={style.postBody}>
                                    <div className={style.postMeta}>
                                        <FontAwesomeIcon icon={faClock} />{" "}
                                        {timeAgo(p.createdAt)}
                                        {p.editedAt ? " (editado)" : ""}
                                    </div>
                                    <div className={style.postContent}>
                                        {p.content
                                            .split("\n")
                                            .map((line, i) => (
                                                <p key={i}>{line || " "}</p>
                                            ))}
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>
                )}

                {!activeThread.locked && (
                    <div className={style.replyBox}>
                        <div className={style.replyTitle}>
                            <FontAwesomeIcon icon={faReply} /> Responder
                        </div>
                        {this.renderCharSelector()}
                        <textarea
                            className={style.replyTextarea}
                            placeholder="Escribí tu respuesta..."
                            value={formContent}
                            onChange={e =>
                                this.setState({
                                    formContent: e.target.value
                                })
                            }
                            maxLength={4000}
                            rows={5}
                        />
                        {formError ? (
                            <div className={style.formError}>{formError}</div>
                        ) : null}
                        <div className={style.replyActions}>
                            <button
                                type="button"
                                className={style.submitBtn}
                                onClick={this.submitReply}
                                disabled={formSubmitting}
                            >
                                {formSubmitting
                                    ? "Publicando..."
                                    : "Publicar respuesta"}
                            </button>
                        </div>
                    </div>
                )}
            </React.Fragment>
        );
    };

    renderNewThreadView = () => {
        const {
            activeCategory,
            formTitle,
            formContent,
            formSubmitting,
            formError
        } = this.state;
        return (
            <div className={style.newThreadBox}>
                <h2 className={style.newThreadTitle}>
                    Nuevo tema en{" "}
                    <span className={style.newThreadCategory}>
                        {activeCategory && activeCategory.name}
                    </span>
                </h2>
                {this.renderCharSelector()}
                <input
                    type="text"
                    className={style.titleInput}
                    placeholder="Título del tema..."
                    value={formTitle}
                    onChange={e =>
                        this.setState({ formTitle: e.target.value })
                    }
                    maxLength={120}
                />
                <textarea
                    className={style.contentTextarea}
                    placeholder="Escribí algo interesante..."
                    value={formContent}
                    onChange={e =>
                        this.setState({ formContent: e.target.value })
                    }
                    maxLength={4000}
                    rows={10}
                />
                {formError ? (
                    <div className={style.formError}>{formError}</div>
                ) : null}
                <div className={style.formActions}>
                    <button
                        type="button"
                        className={style.cancelBtn}
                        onClick={() => this.fetchThreads(activeCategory)}
                    >
                        Cancelar
                    </button>
                    <button
                        type="button"
                        className={style.submitBtn}
                        onClick={this.submitNewThread}
                        disabled={formSubmitting}
                    >
                        {formSubmitting ? "Publicando..." : "Publicar tema"}
                    </button>
                </div>
            </div>
        );
    };

    render() {
        const { view, activeCategory, activeThread } = this.state;

        // Breadcrumb dinámico — depende del view actual.
        const breadcrumb = (
            <div className={style.breadcrumb}>
                <button
                    type="button"
                    className={style.crumb}
                    onClick={() =>
                        this.setState({
                            view: "categories",
                            activeCategory: null,
                            activeThread: null
                        })
                    }
                >
                    Foro
                </button>
                {(view === "threads" ||
                    view === "thread" ||
                    view === "newThread") &&
                    activeCategory && (
                        <React.Fragment>
                            <span className={style.crumbSep}>›</span>
                            <button
                                type="button"
                                className={style.crumb}
                                onClick={() => this.fetchThreads(activeCategory)}
                            >
                                {activeCategory.name}
                            </button>
                        </React.Fragment>
                    )}
                {view === "thread" && activeThread && (
                    <React.Fragment>
                        <span className={style.crumbSep}>›</span>
                        <span className={style.crumbCurrent}>
                            {activeThread.title}
                        </span>
                    </React.Fragment>
                )}
                {view === "newThread" && (
                    <React.Fragment>
                        <span className={style.crumbSep}>›</span>
                        <span className={style.crumbCurrent}>Nuevo tema</span>
                    </React.Fragment>
                )}
            </div>
        );

        return (
            <MainContainer userAgent={this.props.userAgent}>
                <div className={style.page}>
                    <div className={style.card}>
                        <div className={style.cardHeader}>
                            <FontAwesomeIcon icon={faComments} />
                            <h2>Foro</h2>
                            {view !== "categories" && (
                                <button
                                    type="button"
                                    className={style.backBtn}
                                    onClick={this.goBack}
                                >
                                    <FontAwesomeIcon icon={faArrowLeft} />{" "}
                                    Atrás
                                </button>
                            )}
                        </div>

                        {breadcrumb}

                        <div className={style.content}>
                            {view === "categories" &&
                                this.renderCategoriesView()}
                            {view === "threads" && this.renderThreadsView()}
                            {view === "thread" && this.renderThreadView()}
                            {view === "newThread" &&
                                this.renderNewThreadView()}
                        </div>
                    </div>
                </div>
            </MainContainer>
        );
    }
}

function mapStateToProps(state) {
    return { account: state.account };
}

export default connect(mapStateToProps)(ForumPage);
